import React, { FC, CSSProperties } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Typography } from '@mui/material';
import { useStyles } from './styles';

type MarkdownContentProps = {
  content: string;
  textColor?: CSSProperties["color"];
};

export const MarkdownContent: FC<MarkdownContentProps> = ({
  content,
  textColor = "white",
}) => {
  const classes = useStyles({ textColor });

  return (
    <div className={classes.markdownContainer}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => (
            <Typography variant="body2" className={classes.paragraph}>
              {children}
            </Typography>
          ),
          h1: ({ children }) => (
            <Typography variant="h6" className={classes.heading}>
              {children}
            </Typography>
          ),
          h2: ({ children }) => (
            <Typography variant="body1" className={classes.subheading}>
              {children}
            </Typography>
          ),
          h3: ({ children }) => (
            <Typography variant="body2" className={classes.subheading}>
              <strong>{children}</strong>
            </Typography>
          ),
          strong: ({ children }) => (
            <strong style={{ color: textColor }}>{children}</strong>
          ),
          em: ({ children }) => (
            <em style={{ color: textColor }}>{children}</em>
          ),
          code: ({ children, ...props }: any) => (
            <code className={(props.inline) ? classes.inlineCode : classes.codeBlock}>
              {children}
            </code>
          ),
          pre: ({ children }) => (
            <pre className={classes.preBlock}>
              {children}
            </pre>
          ),
          ul: ({ children }) => (
            <ul className={classes.list}>
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className={classes.orderedList}>
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className={classes.listItem}>
              {children}
            </li>
          ),
          blockquote: ({ children }) => (
            <blockquote className={classes.blockquote}>
              {children}
            </blockquote>
          ),
          table: ({ children }) => (
            <table className={classes.table}>
              {children}
            </table>
          ),
          thead: ({ children }) => (
            <thead className={classes.tableHead}>
              {children}
            </thead>
          ),
          tbody: ({ children }) => (
            <tbody className={classes.tableBody}>
              {children}
            </tbody>
          ),
          tr: ({ children }) => (
            <tr className={classes.tableRow}>
              {children}
            </tr>
          ),
          th: ({ children }) => (
            <th className={classes.tableHeader}>
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className={classes.tableCell}>
              {children}
            </td>
          ),
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer" className={classes.link}>
              {children}
            </a>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};
